import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from '@apollo/client';
import { RestLink } from 'apollo-link-rest';
import env from 'bench-environment';

import { makeReportTypePatcher } from './graphqlClientTypePatchers';

export const graphqlServiceHttpLink = new HttpLink({
  uri: `${env.location.apiRoot}/graphql/query`,
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export const serverlessGraphqlServiceHttpLink = new HttpLink({
  uri: `${env.location.v2GraphqlRoot}`,
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const restLink = new RestLink({
  uri: `${env.location.apiRoot}/api/v1/`, // default if endpoint not specified in query
  endpoints: {
    financialData: `${env.location.apiRoot}/financial-data/api/`,
    v2FinancialData: env.location.v2FinancialDataApiRoot,
    mainapp: `${env.location.apiRoot}/api/v1/`,
    gateway: `${env.location.apiRoot}/gateway/web/api/`,
    reports: `${env.location.apiRoot}/reports/api/`,
  },
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const reportingServiceRestLink = new RestLink({
  uri: `${env.location.apiRoot}/reports/api/`,
  endpoints: {
    financialData: `${env.location.apiRoot}/financial-data/api/`,
    v2FinancialData: env.location.v2FinancialDataApiRoot,
    mainapp: `${env.location.apiRoot}/api/v1/`,
    stripe: `${env.location.apiRoot}/stripe/api/`,
  },
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  typePatcher: {
    BalanceSheetReport: makeReportTypePatcher(
      'BalanceSheetLedgerSummary',
      'BalanceSheetLedgerCategorySummary',
      'balanceInCents',
      'balanceSheet',
    ),
    IncomeStatementReport: makeReportTypePatcher(
      'IncomeStatementLedgerSummary',
      'IncomeStatementLedgerCategorySummary',
      'sumTotalInCents',
      'incomeStatement',
    ),
  },
});

const withV2 = link =>
  ApolloLink.split(op => !!op.getContext().useV2, serverlessGraphqlServiceHttpLink, link);

const typePolicies = {
  Query: {
    fields: {
      ledgerAccounts: {
        keyArgs: false,
      },
      taxFilings: {
        keyArgs: ['filter', 'limit', 'offset'],
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LedgerAccount: {
    fields: {
      dataEntries: {
        keyArgs: ['filter', ['startDate', 'endDate', 'query', 'sort', ['field', 'direction']]],
        merge(existing, incoming, { cache }) {
          const merged = [...(existing?.dataEntries || [])];

          incoming?.dataEntries.forEach(entry => {
            const key = cache.identify(entry);
            if (!merged.some(entry => cache.identify(entry) === key)) {
              merged.push(entry);
            }
          });

          return {
            dataEntries: merged,
            pageInfo: { ...(existing?.pageInfo || {}), ...(incoming?.pageInfo || {}) },
          };
        },
      },
    },
  },
};

const graphqlClient = new ApolloClient({
  link: ApolloLink.from([reportingServiceRestLink, withV2(graphqlServiceHttpLink)]),
  cache: new InMemoryCache({
    typePolicies,
  }),
});

export const graphqlRestClient = new ApolloClient({
  link: ApolloLink.from([restLink, withV2(graphqlServiceHttpLink)]),
  cache: new InMemoryCache({
    typePolicies,
  }),
});

export default graphqlClient;
